// 重新驗證（plan/vms-web-v0.md AUTH-05）：API 回 403 REAUTH_REQUIRED → SDK 呼叫這裡的 handler →
// 跳對話框（密碼 + 驗證碼）→ /session/reauth 換上新 token → SDK 重送原請求。
// 開的時候讀這個帳號有沒有開兩步驟驗證、有沒有 passkey：沒開 TOTP 就不出現驗證碼欄（伺服器回 MFA_REQUIRED 才補出來），
// 沒有 passkey 就不出現〔用 passkey 驗證〕；讀不到照舊兩個都出現（驗證碼標「有開才要填」）。
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { setStepUpHandler, KinioError } from '@kinio/web-sdk';
import { Dialog } from './Dialog';
import { listPasskeys, mfaStatus, passkeyErrorCode, reauth, reauthWithPasskey, SessionError } from '../lib/session';
import { passkeySupported } from '../lib/webauthn';
import { PasswordInput } from './PasswordInput';

export function StepUpHost() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  // 'unknown' = 還沒讀到 / 讀不到；'on' = 開了 TOTP；'off' = 沒開
  const [totp, setTotp] = useState<'unknown' | 'on' | 'off'>('unknown');
  const [hasPasskey, setHasPasskey] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  useEffect(() => {
    setStepUpHandler(
      () =>
        new Promise<boolean>((resolve) => {
          resolver.current = resolve;
          setPassword('');
          setCode('');
          setErr(null);
          setTotp('unknown');
          setHasPasskey(true);
          setOpen(true);
          mfaStatus()
            .then((m) => setTotp(m.totp_enabled === true ? 'on' : m.totp_enabled === false ? 'off' : 'unknown'))
            .catch(() => setTotp('unknown'));
          listPasskeys()
            .then((l) => setHasPasskey(l.length > 0))
            .catch(() => setHasPasskey(true));
        }),
    );
    return () => setStepUpHandler(null);
  }, []);

  function done(ok: boolean) {
    setOpen(false);
    resolver.current?.(ok);
    resolver.current = null;
  }

  async function withPasskey() {
    setBusy(true);
    setErr(null);
    try {
      await reauthWithPasskey();
      done(true);
    } catch (x) {
      const c = passkeyErrorCode(x);
      setErr(t(`passkey.err_${c}`, { code: c, defaultValue: t('passkey.err_default', { code: c }) }));
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await reauth(password, code);
      done(true);
    } catch (x) {
      const c = x instanceof SessionError || x instanceof KinioError ? x.code : 'default';
      if (c === 'MFA_REQUIRED') setTotp('on');
      setErr(t(`mfa.err_${c}`, { code: c, defaultValue: t('mfa.err_default', { code: c }) }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} title={t('stepup.title')} onClose={() => done(false)}>
      <form className="form" onSubmit={submit}>
        <p className="muted">{t('stepup.body')}</p>
        {passkeySupported() && hasPasskey && (
          <>
            <button type="button" className="btn" disabled={busy} onClick={() => void withPasskey()}>
              {t('passkey.verify')}
            </button>
            <div className="divider">
              <span>{t('passkey.or_password')}</span>
            </div>
          </>
        )}
        <label className="field">
          <span>{t('auth.password')}</span>
          <PasswordInput autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        {totp !== 'off' && (
          <label className="field">
            <span>{t(totp === 'on' ? 'stepup.code' : 'stepup.code_optional')}</span>
            <input className="input mono" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" />
            <span className="small muted">{t(totp === 'on' ? 'stepup.code_hint_on' : 'stepup.code_hint')}</span>
          </label>
        )}
        {err && (
          <div className="error" role="alert">
            {err}
          </div>
        )}
        <div className="row-end">
          <button type="button" className="btn" onClick={() => done(false)}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {t('stepup.submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
